import { onAuthStateChanged } from 'firebase/auth';
import React, { useEffect, useState } from 'react';

import { confirmJiraProjects, fetchUserProjects, UserProjectsData } from '../shared/api/integration';
import { auth } from '../shared/firebase/firebase';

const SYNC_PRESETS = [
  { label: 'Last month', months: 1 },
  { label: 'Last 3 months', months: 3 },
  { label: 'Last 6 months', months: 6 },
  { label: 'Last year', months: 12 },
  { label: 'Last 2 years', months: 24 },
] as const;

const syncFromDate = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  missing_state: 'Connection failed: the request was missing a required parameter. Please try again.',
  user_not_found: 'Connection failed: your account could not be verified. Please log out and try again.',
  cloud_info_failed:
    'Connection failed: could not retrieve your Jira workspace. Please check your Jira permissions and try again.',
};

const getOAuthError = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('error');
  if (!code) return null;
  return OAUTH_ERROR_MESSAGES[code] ?? 'Connection failed. Please try again.';
};

const ConnectJiraPage: React.FC = () => {
  const [data, setData] = useState<UserProjectsData | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncPreset, setSyncPreset] = useState<number>(12);

  useEffect(() => {
    const oauthError = getOAuthError();
    if (oauthError) {
      setError(oauthError);
      setIsLoading(false);
      return;
    }

    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (!user) {
        setError('Not authenticated. Please close this window and try again.');
        setIsLoading(false);
        return;
      }

      // If opened from the onboarding stepper, skip project selection —
      // the stepper handles it in the next step.
      if (localStorage.getItem('jira_connect_mode') === 'stepper') {
        localStorage.removeItem('jira_connect_mode');
        if (window.opener) window.opener.postMessage({ type: 'jira-connected' }, '*');
        window.close();
        return;
      }

      fetchUserProjects()
        .then((d) => {
          setData(d);
          setSelected(new Set(d.selectedProjectIds));
        })
        .catch(() => setError('Failed to load projects. Please close this window and try again.'))
        .finally(() => setIsLoading(false));
    });
  }, []);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const overLimit = data?.planLimit !== null && data?.planLimit !== undefined && selected.size > data.planLimit;

  const handleConfirm = async () => {
    if (!data || overLimit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await confirmJiraProjects(Array.from(selected), syncFromDate(syncPreset));
      if (window.opener) window.opener.postMessage({ type: 'jira-connected' }, '*');
      window.close();
    } catch {
      setError('Failed to save project selection. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => window.close();

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body gap-4">
          <div>
            <h2 className="card-title text-lg font-display">
              {error && !data ? 'Jira Connection Error' : 'Select Jira Projects'}
            </h2>
            {(!error || data) && (
              <p className="text-sm opacity-70 mt-1">
                Choose which projects MFlow should sync. Only selected projects will be tracked.
              </p>
            )}
          </div>

          {isLoading && (
            <div className="flex justify-center py-6">
              <span className="loading loading-spinner loading-md" />
            </div>
          )}

          {!isLoading && error && (
            <>
              <div className="alert alert-error">
                <span className="text-sm">{error}</span>
              </div>
              {!data && (
                <div className="card-actions justify-end pt-2">
                  <button className="btn btn-ghost btn-sm" onClick={() => window.close()}>
                    Close
                  </button>
                </div>
              )}
            </>
          )}

          {!isLoading && data && (
            <>
              {data.planLimit !== null && (
                <div className={`alert ${overLimit ? 'alert-error' : 'alert-info'} py-2`}>
                  <span className="text-sm">
                    {overLimit
                      ? `Your plan allows ${data.planLimit} project${data.planLimit === 1 ? '' : 's'}. Deselect ${selected.size - data.planLimit} to continue.`
                      : `${data.plan} plan — up to ${data.planLimit} project${data.planLimit === 1 ? '' : 's'} (${selected.size} selected)`}
                  </span>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {data.availableProjects.length === 0 && (
                  <p className="text-sm opacity-60 text-center py-4">No projects found in your Jira account.</p>
                )}
                {data.availableProjects.map((p) => (
                  <label
                    key={p.id}
                    aria-label={p.name}
                    className="flex items-center gap-3 cursor-pointer hover:bg-base-200 rounded p-2"
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm checkbox-primary"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{p.name}</div>
                      <div className="text-xs opacity-50">{p.key}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="space-y-1">
                <label htmlFor="connect-jira-sync-from" className="text-xs font-medium opacity-70">
                  Sync history from
                </label>
                <select
                  id="connect-jira-sync-from"
                  className="select select-bordered select-xs w-full"
                  value={syncPreset}
                  onChange={(e) => setSyncPreset(Number(e.target.value))}
                >
                  {SYNC_PRESETS.map((p) => (
                    <option key={p.months} value={p.months}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs opacity-40">Changelog data before this date won&apos;t be imported.</p>
              </div>

              <div className="card-actions justify-between pt-2">
                <button className="btn btn-ghost btn-sm" onClick={handleSkip} disabled={isSubmitting}>
                  Skip for now
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleConfirm}
                  disabled={isSubmitting || overLimit || selected.size === 0}
                >
                  {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : null}
                  Confirm
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectJiraPage;
